<script setup lang="ts">
//#region imports
import { computed, h, nextTick, ref, resolveComponent } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { omit } from 'lodash-es';
import { usePermissions } from '@/composables/usePermissions';
import { useApi } from '@/api';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import {
  useTableFilter,
  useTablePaginator,
  useTableSearch,
  useTableSort,
} from '@/composables/useTableLS';
import { useLodash } from '@/setup/lodash';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import type { SettingsPageQuery } from '@/types/core';
import type {
  GetAffiliatesResponse,
  GetLanguagesResponse,
  GetMarketingMaterialsData,
  GetMarketingMaterialsResponse,
} from '@/types/openapi-ts';
import type { UiTableNewField, UiTableNewFilter, UiTableNewSortEmit } from '@/types/table';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type MarketingMaterialsQuery = SettingsPageQuery<GetMarketingMaterialsData['query']>;

type RowType = GetMarketingMaterialsResponse['payload'][number];
type AffiliateId = string | Array<string> | null;

interface OptionsData {
  languagesList: GetLanguagesResponse['payload'];
  affiliatesList: GetAffiliatesResponse['payload'];
}
//#endregion

//#region const
const LS_PATH = 'MarketingMaterials';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<GetMarketingMaterialsResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'type',
    computedValue: (row) => t(`marketingMaterials.fields.type.${row.type}`),
  },
  {
    name: 'brand_visibility',
    width: '100',
    linkAppend: (row) => renderBrands(row),
  },
  {
    name: 'language_codes',
    width: '290',
    linkAppend: (row) => renderLanguages(row, 12),
  },
  {
    name: 'visibility',
    computedValue: (row) => t(`marketingMaterials.fields.visibility.${row.visibility}`),
  },
  {
    name: 'impressions_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
    width: '95',
  },
  {
    name: 'visits_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
    width: '95',
  },
  {
    name: 'registrations_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
    width: '95',
  },
  {
    name: 'conversion_rate',
    align: 'right',
    sortable: 'custom',
    format: 'percent',
    width: '110',
  },
  {
    name: 'status',
    type: 'status',
    statusText: (value) => t(`marketingMaterials.fields.status.${value}`),
    status: (value) => value,
    width: '80',
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
  {
    name: 'updated_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];

const canBeUndefined = [
  'status',
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const updateUrl = useUpdateUrl();
const store = useTypedStore();
const _ = useLodash();
const popupManager = usePopupManager();
const { t } = useI18n();
const { hasPermission } = usePermissions();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
});
//#endregion

//#region state
const tableData = ref<GetMarketingMaterialsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);

const notApplyFilterState = ref<UiTableNewFilter['filter']>({});

const isSelectLoading = ref(false);
const options = ref<OptionsData>({
  languagesList: [],
  affiliatesList: [],
});
//#endregion

//#region computed
const brands = computed(() => store.getters['misc/brands']);

const canEditMarketingMaterial = computed(() => {
  return hasPermission('marketing_materials_edit');
});

const selectComponentCommonParams = computed(() => {
  return {
    loading: isSelectLoading.value,
    remote: true,
  };
});

const marketingMaterialTypeList = computed(() => {
  return [
    {
      id: 'html5_banner',
      name: t('ui.filters.select.html5_banner'),
    },
    {
      id: 'simple_banner',
      name: t('ui.filters.select.simple_banner'),
    },
    {
      id: 'landing',
      name: t('ui.filters.select.landing'),
    },
  ];
});

const marketingMaterialStatusList = computed(() => {
  return [
    {
      value: 'undefined',
      label: t('ui.filters.select.all'),
    },
    {
      value: 'active',
      label: t('ui.filters.select.active'),
    },
    {
      value: 'inactive',
      label: t('ui.filters.select.inactive'),
    },
  ];
});

const availabilityMaterialsList = computed(() => {
  return [
    {
      id: 'public',
      name: t('ui.filters.select.public'),
    },
    {
      id: 'private',
      name: t('ui.filters.select.private'),
    },
    {
      id: 'hidden',
      name: t('ui.filters.select.hidden'),
    },
  ];
});

const filtersList = computed(() => {
  const filters = [
    {
      id: 'visibility',
      name: 'availabilityMaterials',
      type: 'select',
      params: {
        list: availabilityMaterialsList.value,
      },
    },
    {
      id: 'affiliate_id',
      name: 'affiliate',
      type: 'select',
      show: notApplyFilterState.value?.visibility && notApplyFilterState.value?.visibility === 'private' ? 'show' : 'hide',
      params: {
        list: options.value.affiliatesList,
        optionLabelParam: 'email',
        optionNameParam: 'email',
        multiple: true,
        'collapse-tag-tooltip': true,
        'remote-method': getAffiliates,
        ...selectComponentCommonParams.value,
      },
    },
    {
      id: 'type',
      name: 'marketingMaterialType',
      type: 'select',
      params: {
        list: marketingMaterialTypeList.value,
      },
    },
    {
      id: 'status',
      name: 'marketingMaterialStatus',
      type: 'radio',
      params: {
        options: marketingMaterialStatusList.value,
      },
    },
    {
      id: 'language_code',
      name: 'languageCode',
      type: 'select',
      params: {
        list: options.value.languagesList,
        optionValueParam: 'code',
        'remote-method': getLanguages,
        ...selectComponentCommonParams.value,
      },
    },
    {
      id: 'site_id',
      name: 'brand',
      type: 'select',
      params: {
        list: brands.value,
        multiple: true,
        filterable: false,
        collapseTagTooltip: true,
      },
    },
  ];

  return filters.filter((item) =>  !item.show || item.show === 'show');
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    setFilter(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);
  }
  nextTick(() => {
    const affiliateId = filterParams.value.affiliate_id as string | string[];

    if (affiliateId && affiliateId.length > 0) {
      getAffiliates(undefined, filterParams.value.affiliate_id as AffiliateId);
    }
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as GetMarketingMaterialsData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: MarketingMaterialsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();
  options = {
    ...options,
    ...filterParams.value,
  };

  try {
    const { data: { payload, misc } } = await api.getMarketingMaterials(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function handleDelete(row: RowType) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('marketingMaterials.buttons.delete'),
      message: t('marketingMaterials.messages.delete', { name: row.name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteMarketingMaterial(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}

async function openMarketingMaterialPopup(id = null) {
  popupManager.marketingMaterialPopup.open({
    data: {
      materialId: id,
    },
    listeners: {
      success: () => {
        fetchData(true);
      },
    },
  });
}

function getMarketingMaterialsFilter(data: UiTableNewFilter['filter']) {
  filterParams.value = data;
  handleRefresh();
}

async function getAffiliates(searchValue: string | undefined, ids: AffiliateId) {
  isSelectLoading.value = true;
  let query = {
    search: searchValue || undefined,
    id: ids || undefined,
    limit: !ids ? 100 : undefined,
  };

  try {
    const response = await api.getAffiliates(query);
    options.value.affiliatesList = response.data.payload;
  } catch (error) {
    console.error(error);
  } finally {
    isSelectLoading.value = false;
  }
}

async function getLanguages(searchValue: string) {
  isSelectLoading.value = true;

  let query = {
    search: searchValue || undefined,
  };

  try {
    const response = await api.getLanguages(query);
    options.value.languagesList = response.data.payload;
  } catch (error) {
    console.error(error);
  } finally {
    isSelectLoading.value = false;
  }
}

function renderBrands(item: RowType) {
  if (item.brand_visibility === 'all') {
    return h('div', {class: 't:truncate'}, t('marketingMaterials.fields.brand_visibility.all'));
  }

  const hasAdditional = item.visible_brands.length > 1;
  const firstBrand = item.visible_brands.slice(0, 1)[0]!.site_name;

  if (!hasAdditional) {
    return h('div', {class: 't:truncate'}, firstBrand);
  }

  const Button = resolveComponent('ui-button');
  const UiDropdown = resolveComponent('ui-dropdown');
  const otherBrands = item.visible_brands.slice(1).map((brand) => brand.site_name);

  const dropdown = h(
    UiDropdown,
    {
      itemsList: otherBrands,
    },
    {
      default: () => h(Button,  {size: 'small', class: 't:ml-4'}, {
        default: () => `+${otherBrands.length}`,
      }),
    },
  );

  return h('div', { class: 't:flex t:items-center t:w-full' }, [firstBrand, dropdown]);
}

function renderLanguages(item: RowType, count: number) {
  const visibleLanguages = item.language_codes.slice(0, count).join(', ');

  if (item.language_codes.length <= count) {
    return h('div', {class: 't:truncate'}, visibleLanguages);
  }

  const Button = resolveComponent('ui-button');
  const UiDropdown = resolveComponent('ui-dropdown');
  const otherLanguages = item.language_codes.slice(count).map((language) => language);

  const dropdown = h(
    UiDropdown,
    {
      itemsList: otherLanguages,
    },
    {
      default: () => h(Button,  {size: 'small', class: 't:ml-4'}, {
        default: () => `+${otherLanguages.length}`,
      }),
    },
  );

  return h('div', { class: 't:flex t:items-center t:w-full' }, [visibleLanguages, dropdown]);
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="marketing-materials">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('marketingMaterials.pageTitle') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          :can-be-undefined="canBeUndefined"
          @submit="getMarketingMaterialsFilter"
          @not-apply-state="(state: UiTableNewFilter['filter']) => notApplyFilterState = state"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditMarketingMaterial"
          filled
          icon="plus"
          @click="openMarketingMaterialPopup()"
        >
          {{ $t('marketingMaterials.buttons.add_material') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="marketingMaterials"
      @sort="handleSort"
    >
      <template
        v-if="canEditMarketingMaterial"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="pen"
              :tooltip="$t('marketingMaterials.buttons.edit')"
              @click="openMarketingMaterialPopup(scope.row.id)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('marketingMaterials.buttons.delete')"
              :disabled="scope.row.platform_service_domains_count > 0"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
